import { Theme } from "@/types/themes";

// Define the new layout and action location types
export type ThemeLayout = "sidebar" | "topbar" | "focus" | "dock";
export type ActionLocation = "top-right" | "header" | "bottom-center" | "fab";

export interface ExtendedTheme extends Theme {
  variant: "light" | "dark";
  layout: ThemeLayout;
  actionLocation: ActionLocation;
  css?: {
    radius?: string; // e.g. '0px' for Obsidian
    fontFamily?: string;
  };
}

const themes: Record<string, { light: ExtendedTheme; dark: ExtendedTheme }> = {
  // 1. MINIMALIST (Premium SaaS Standard)
  MINIMALIST: {
    light: {
      name: "Minimalist",
      variant: "light",
      layout: "sidebar",
      actionLocation: "top-right",
      css: { radius: "0px" },
      logo: "/brand/novari-mark.png?v=3",
      colorPalette: {
        base: "#FCFDFD",
        neutral: "#F8FAFC", // Slate-50
        textPrimary: "#0F172A", // Ink
        textSecondary: "#475569", // Slate-600 (better legibility)

        accentPrimary: "#0F172A",
        accentSecondary: "#F1F5F9", // Slate-100
        accentTertiary: "#CBD5E1", // Slate-300

        error: "#DC2626",
        warning: "#F59E0B",
        success: "#10B981",
        info: "#3B82F6",
      },
      typography: {
        headings: {
          h1: "Inter, sans-serif",
          h2: "Inter, sans-serif",
          h3: "Inter, sans-serif",
          h4: "Inter, sans-serif",
          h5: "Inter, sans-serif",
          h6: "Inter, sans-serif",
        },
        body: {
          large: "Inter, sans-serif",
          medium: "Inter, sans-serif",
          small: "Inter, sans-serif",
        },
        rounding: 24,
      },
      surface: {
        card: "bg-white ring-1 ring-slate-900/5 shadow-sm",
        glassDock: "bg-white/80 backdrop-blur-xl ring-1 ring-slate-900/5",
        modal: "bg-white ring-1 ring-slate-900/5 shadow-xl",
        tooltip: "bg-slate-900 text-slate-50",
        dropdown: "bg-white ring-1 ring-slate-900/5 shadow-lg",
      },
      backgroundAnimation: { type: "none", speed: 0, intensity: 0, colors: [] },
      componentLayouts: {
        Sidebar: {
          width: "240px",
          position: "left",
          background: "standard",
          size: "h-screen",
          items: [],
          activeState: "emerald",
        },
        Header: { height: "auto", background: "white", shadow: "sm" },
        MainGrid: { columns: 12, gap: "6", padding: "8" },
        Footer: {
          height: "auto",
          background: "transparent",
          borderTop: "none",
        },
      },
      featurePositionMap: {},
    },
    dark: {
      name: "Minimalist",
      variant: "dark",
      layout: "sidebar",
      actionLocation: "top-right",
      css: { radius: "0px" },
      logo: "/brand/novari-mark.png?v=3",
      colorPalette: {
        base: "#020617", // Slate-950 (Deeper than Ink for premium feel)
        neutral: "#0f172a", // Slate-900
        surface2: "#1E293B",
        textPrimary: "#F8FAFC",
        textSecondary: "#94A3B8",

        accentPrimary: "#F8FAFC",
        accentSecondary: "#1E293B", // Slate-800
        accentTertiary: "#334155", // Slate-700

        error: "#EF4444",
        warning: "#F59E0B",
        success: "#10B981",
        info: "#3B82F6",
      },
      typography: {
        headings: {
          h1: "Inter, sans-serif",
          h2: "Inter, sans-serif",
          h3: "Inter, sans-serif",
          h4: "Inter, sans-serif",
          h5: "Inter, sans-serif",
          h6: "Inter, sans-serif",
        },
        body: {
          large: "Inter, sans-serif",
          medium: "Inter, sans-serif",
          small: "Inter, sans-serif",
        },
        rounding: 24,
      },
      surface: {
        card: "bg-slate-900/40 ring-1 ring-white/5 backdrop-blur-sm",
        glassDock: "bg-slate-950/80 backdrop-blur-xl ring-1 ring-white/5",
        modal: "bg-slate-950 ring-1 ring-white/10 shadow-2xl",
        tooltip: "bg-slate-50 text-slate-900",
        dropdown: "bg-slate-900 ring-1 ring-white/5 shadow-lg",
      },
      backgroundAnimation: { type: "none", speed: 0, intensity: 0, colors: [] },
      componentLayouts: {
        Sidebar: {
          width: "240px",
          position: "left",
          background: "standard",
          size: "h-screen",
          items: [],
          activeState: "emerald",
        },
        Header: { height: "auto", background: "standard", shadow: "none" },
        MainGrid: { columns: 12, gap: "6", padding: "8" },
        Footer: {
          height: "auto",
          background: "transparent",
          borderTop: "none",
        },
      },
      featurePositionMap: {},
    },
  },



  // 3. MONOCHROME (Pure Black & White)
  MONOCHROME: {
    light: {
      name: "Monochrome",
      variant: "light",
      layout: "sidebar",
      actionLocation: "top-right",
      css: { radius: "0px" },
      logo: "/brand/novari-mark.png?v=3",
      colorPalette: {
        base: "#FAFAFA",
        neutral: "#F8FAFC", // Slate-50
        textPrimary: "#111111",
        textSecondary: "#475569", // Slate-600
        accentPrimary: "#111111",
        accentSecondary: "#E2E8F0", // Slate-200
        accentTertiary: "#94A3B8", // Slate-400
        error: "#DC2626",
        warning: "#F59E0B",
        success: "#10B981",
        info: "#3B82F6",
      },
      typography: {
        headings: {
          h1: "Inter, sans-serif",
          h2: "Inter, sans-serif",
          h3: "Inter, sans-serif",
          h4: "Inter, sans-serif",
          h5: "Inter, sans-serif",
          h6: "Inter, sans-serif",
        },
        body: {
          large: "Inter, sans-serif",
          medium: "Inter, sans-serif",
          small: "Inter, sans-serif",
        },
        rounding: 16,
      },
      surface: {
        card: "bg-white border border-slate-200 shadow-none",
        glassDock: "bg-white/90 backdrop-blur-md border border-slate-200",
        modal: "bg-white border border-slate-200 shadow-none",
        tooltip: "bg-black text-white",
        dropdown: "bg-white border border-slate-200 shadow-none",
      },
      backgroundAnimation: { type: "none", speed: 0, intensity: 0, colors: [] },
      componentLayouts: {
        Sidebar: {
          width: "240px",
          position: "left",
          background: "standard",
          size: "h-screen",
          items: [],
          activeState: "emerald",
        },
        Header: { height: "auto", background: "white", shadow: "none" },
        MainGrid: { columns: 12, gap: "6", padding: "8" },
        Footer: {
          height: "auto",
          background: "transparent",
          borderTop: "none",
        },
      },
      featurePositionMap: {},
    },
    dark: {
      name: "Monochrome",
      variant: "dark",
      layout: "sidebar",
      actionLocation: "top-right",
      css: { radius: "0px" },
      logo: "/brand/novari-mark.png?v=3",
      colorPalette: {
        base: "#111118",      // Very dark slate, not pure black
        neutral: "#1C1C26",   // Slightly elevated surface
        surface2: "#28283A",
        textPrimary: "#E8E8F2",  // Slightly warm off-white, easier on eyes
        textSecondary: "#9090A8", // Muted slate-purple
        accentPrimary: "#C8C8E0", // Soft lavender-white
        accentSecondary: "#28283A", // Subtle surface highlight
        accentTertiary: "#48485C", // Medium divider
        error: "#F87171",     // Soft red (legible on dark)
        warning: "#FBBF24",   // Amber
        success: "#6EE7B7",   // Soft emerald
        info: "#93C5FD",      // Soft blue
      },
      typography: {
        headings: {
          h1: "Inter, sans-serif",
          h2: "Inter, sans-serif",
          h3: "Inter, sans-serif",
          h4: "Inter, sans-serif",
          h5: "Inter, sans-serif",
          h6: "Inter, sans-serif",
        },
        body: {
          large: "Inter, sans-serif",
          medium: "Inter, sans-serif",
          small: "Inter, sans-serif",
        },
        rounding: 16,
      },
      surface: {
        card: "bg-neutral-900 border border-white/10 shadow-none",
        glassDock: "bg-black/90 backdrop-blur-md border border-white/10",
        modal: "bg-black border border-white/20 shadow-none",
        tooltip: "bg-white text-black",
        dropdown: "bg-black border border-white/20 shadow-none",
      },
      backgroundAnimation: { type: "none", speed: 0, intensity: 0, colors: [] },
      componentLayouts: {
        Sidebar: {
          width: "240px",
          position: "left",
          background: "standard",
          size: "h-screen",
          items: [],
          activeState: "emerald",
        },
        Header: { height: "auto", background: "standard", shadow: "none" },
        MainGrid: { columns: 12, gap: "6", padding: "8" },
        Footer: {
          height: "auto",
          background: "transparent",
          borderTop: "none",
        },
      },
      featurePositionMap: {},
    },
  },

  // ── INK_EMBER (Novari signature theme) ───────────────────────────────────
  INK_EMBER: {
    light: {
      name: "Ink & Ember",
      variant: "light",
      layout: "sidebar",
      actionLocation: "top-right",
      css: { radius: "1.5rem" },
      logo: "/brand/novari-mark.png?v=3",
      // Paper — warm, low-glare day theme (Novari Experience System)
      colorPalette: {
        base: "#F6F1E7",        // warm paper ground (not white — kinder over long sessions)
        neutral: "#FDFBF6",     // surface
        surface2: "#EFE8DA",    // inset / raised
        textPrimary: "#241E15", // warm ink
        textSecondary: "#6B6154", // ink-2
        accentPrimary: "#C75B3B", // ember — the one loud voice ("do this")
        accentSecondary: "#5E8468", // sage — "you're okay"
        accentTertiary: "#CC8B2E",  // amber — used sparingly
        error: "#C24A34",
        warning: "#C0842B",
        success: "#5E8468",
        info: "#4C6B8A",
      },
      typography: {
        headings: { h1: "Inter, sans-serif", h2: "Inter, sans-serif", h3: "Inter, sans-serif", h4: "Inter, sans-serif", h5: "Inter, sans-serif", h6: "Inter, sans-serif" },
        body: { large: "Inter, sans-serif", medium: "Inter, sans-serif", small: "Inter, sans-serif" },
        rounding: 24,
      },
      surface: {
        card: "bg-white/90 ring-1 ring-slate-900/5 shadow-sm",
        glassDock: "bg-white/80 backdrop-blur-xl ring-1 ring-slate-900/5",
        modal: "bg-white ring-1 ring-slate-900/5 shadow-xl",
        tooltip: "bg-slate-900 text-slate-50",
        dropdown: "bg-white ring-1 ring-slate-900/5 shadow-lg",
      },
      backgroundAnimation: { type: "none", speed: 0, intensity: 0, colors: [] },
      componentLayouts: {
        Sidebar: { width: "240px", position: "left", background: "standard", size: "h-screen", items: [], activeState: "ember" },
        Header: { height: "auto", background: "white", shadow: "sm" },
        MainGrid: { columns: 12, gap: "6", padding: "8" },
        Footer: { height: "auto", background: "transparent", borderTop: "none" },
      },
      featurePositionMap: {},
    },
    dark: {
      name: "Ink & Ember",
      variant: "dark",
      layout: "sidebar",
      actionLocation: "top-right",
      css: { radius: "1.5rem" },
      logo: "/brand/novari-mark.png?v=3",
      // Lamplight — warm, comfortable night theme (a room under a reading lamp, not premium-black)
      colorPalette: {
        base: "#17140E",        // warm brown-black ground
        neutral: "#1F1B14",     // surface
        surface2: "#2A241A",    // raised
        textPrimary: "#F2EBDD", // warm off-white ink
        textSecondary: "#B3A896", // ink-2
        accentPrimary: "#E58A63", // ember (slightly brighter for legibility on dark)
        accentSecondary: "#8FB39B", // sage
        accentTertiary: "#E6B45C",  // amber
        error: "#E27A63",
        warning: "#E0AE5A",
        success: "#8FB39B",
        info: "#8FA9C4",
      },
      typography: {
        headings: { h1: "Inter, sans-serif", h2: "Inter, sans-serif", h3: "Inter, sans-serif", h4: "Inter, sans-serif", h5: "Inter, sans-serif", h6: "Inter, sans-serif" },
        body: { large: "Inter, sans-serif", medium: "Inter, sans-serif", small: "Inter, sans-serif" },
        rounding: 24,
      },
      surface: {
        card: "bg-slate-900/50 ring-1 ring-white/5 backdrop-blur-md shadow-lg",
        glassDock: "bg-slate-950/80 backdrop-blur-xl ring-1 ring-white/5",
        modal: "bg-slate-950 ring-1 ring-white/10 shadow-2xl",
        tooltip: "bg-slate-50 text-slate-900",
        dropdown: "bg-slate-900 ring-1 ring-white/5 shadow-xl",
      },
      backgroundAnimation: { type: "none", speed: 0, intensity: 0, colors: [] },
      componentLayouts: {
        Sidebar: { width: "240px", position: "left", background: "standard", size: "h-screen", items: [], activeState: "ember" },
        Header: { height: "auto", background: "standard", shadow: "none" },
        MainGrid: { columns: 12, gap: "6", padding: "8" },
        Footer: { height: "auto", background: "transparent", borderTop: "none" },
      },
      featurePositionMap: {},
    },
  },

  // ── HIGH_CONTRAST (Pure black/white – accessibility focused) ───────────────
  HIGH_CONTRAST: {
    light: {
      name: "High Contrast",
      variant: "light",
      layout: "sidebar",
      actionLocation: "top-right",
      css: { radius: "0px" },
      logo: "/brand/novari-mark.png?v=3",
      colorPalette: {
        base: "#FAFAFA",
        neutral: "#F0F0F0",
        textPrimary: "#0A0A0A",
        textSecondary: "#111111",
        accentPrimary: "#0A0A0A",
        accentSecondary: "#DDDDDD",
        accentTertiary: "#AAAAAA",
        error: "#CC0000",
        warning: "#997700",
        success: "#006600",
        info: "#0000CC",
      },
      typography: {
        headings: { h1: "Inter, sans-serif", h2: "Inter, sans-serif", h3: "Inter, sans-serif", h4: "Inter, sans-serif", h5: "Inter, sans-serif", h6: "Inter, sans-serif" },
        body: { large: "Inter, sans-serif", medium: "Inter, sans-serif", small: "Inter, sans-serif" },
        rounding: 0,
      },
      surface: {
        card: "bg-white border-2 border-black shadow-none",
        glassDock: "bg-white border-2 border-black",
        modal: "bg-white border-2 border-black shadow-none",
        tooltip: "bg-black text-white",
        dropdown: "bg-white border-2 border-black shadow-none",
      },
      backgroundAnimation: { type: "none", speed: 0, intensity: 0, colors: [] },
      componentLayouts: {
        Sidebar: { width: "240px", position: "left", background: "standard", size: "h-screen", items: [], activeState: "black" },
        Header: { height: "auto", background: "white", shadow: "none" },
        MainGrid: { columns: 12, gap: "6", padding: "8" },
        Footer: { height: "auto", background: "transparent", borderTop: "none" },
      },
      featurePositionMap: {},
    },
    dark: {
      name: "High Contrast",
      variant: "dark",
      layout: "sidebar",
      actionLocation: "top-right",
      css: { radius: "0px" },
      logo: "/brand/novari-mark.png?v=3",
      colorPalette: {
        base: "#0A0A0A",
        neutral: "#111111",
        surface2: "#333333",
        textPrimary: "#FAFAFA",
        textSecondary: "#CCCCCC",
        accentPrimary: "#FAFAFA",
        accentSecondary: "#333333",
        accentTertiary: "#555555",
        error: "#FF4444",
        warning: "#FFCC00",
        success: "#44FF44",
        info: "#4499FF",
      },
      typography: {
        headings: { h1: "Inter, sans-serif", h2: "Inter, sans-serif", h3: "Inter, sans-serif", h4: "Inter, sans-serif", h5: "Inter, sans-serif", h6: "Inter, sans-serif" },
        body: { large: "Inter, sans-serif", medium: "Inter, sans-serif", small: "Inter, sans-serif" },
        rounding: 0,
      },
      surface: {
        card: "bg-black border border-white shadow-none",
        glassDock: "bg-black border border-white",
        modal: "bg-black border border-white shadow-none",
        tooltip: "bg-white text-black",
        dropdown: "bg-black border border-white shadow-none",
      },
      backgroundAnimation: { type: "none", speed: 0, intensity: 0, colors: [] },
      componentLayouts: {
        Sidebar: { width: "240px", position: "left", background: "standard", size: "h-screen", items: [], activeState: "white" },
        Header: { height: "auto", background: "standard", shadow: "none" },
        MainGrid: { columns: 12, gap: "6", padding: "8" },
        Footer: { height: "auto", background: "transparent", borderTop: "none" },
      },
      featurePositionMap: {},
    },
  },

  // ── MIDNIGHT_TEAL (Calm & collected) ───────────────────────────────────────
  MIDNIGHT_TEAL: {
    light: {
      name: "Midnight Teal",
      variant: "light",
      layout: "sidebar",
      actionLocation: "top-right",
      css: { radius: "1.5rem" },
      logo: "/brand/novari-mark.png?v=3",
      colorPalette: {
        base: "#F0FDFA",
        neutral: "#CCFBF1",
        textPrimary: "#134E4A",
        textSecondary: "#0F766E",
        accentPrimary: "#0D9488",
        accentSecondary: "#2DD4BF",
        accentTertiary: "#99F6E4",
        error: "#DC2626",
        warning: "#D97706",
        success: "#059669",
        info: "#0891B2",
      },
      typography: {
        headings: { h1: "Inter, sans-serif", h2: "Inter, sans-serif", h3: "Inter, sans-serif", h4: "Inter, sans-serif", h5: "Inter, sans-serif", h6: "Inter, sans-serif" },
        body: { large: "Inter, sans-serif", medium: "Inter, sans-serif", small: "Inter, sans-serif" },
        rounding: 24,
      },
      surface: {
        card: "bg-teal-50 border border-teal-200 shadow-sm",
        glassDock: "bg-teal-50/90 backdrop-blur-md border border-teal-200/50",
        modal: "bg-teal-50 border border-teal-200 shadow-xl",
        tooltip: "bg-teal-900 text-teal-50",
        dropdown: "bg-teal-50 border border-teal-100 shadow-lg",
      },
      backgroundAnimation: { type: "none", speed: 0, intensity: 0, colors: [] },
      componentLayouts: {
        Sidebar: { width: "240px", position: "left", background: "standard", size: "h-screen", items: [], activeState: "teal" },
        Header: { height: "auto", background: "white", shadow: "sm" },
        MainGrid: { columns: 12, gap: "6", padding: "8" },
        Footer: { height: "auto", background: "transparent", borderTop: "none" },
      },
      featurePositionMap: {},
    },
    dark: {
      name: "Midnight Teal",
      variant: "dark",
      layout: "sidebar",
      actionLocation: "top-right",
      css: { radius: "1.5rem" },
      logo: "/brand/novari-mark.png?v=3",
      colorPalette: {
        base: "#0F172A",
        neutral: "#134E4A",
        surface2: "#0F3340",
        textPrimary: "#F0FDFA",
        textSecondary: "#99F6E4",
        accentPrimary: "#2DD4BF",
        accentSecondary: "#0D9488",
        accentTertiary: "#0F766E",
        error: "#F87171",
        warning: "#FBBF24",
        success: "#34D399",
        info: "#38BDF8",
      },
      typography: {
        headings: { h1: "Inter, sans-serif", h2: "Inter, sans-serif", h3: "Inter, sans-serif", h4: "Inter, sans-serif", h5: "Inter, sans-serif", h6: "Inter, sans-serif" },
        body: { large: "Inter, sans-serif", medium: "Inter, sans-serif", small: "Inter, sans-serif" },
        rounding: 24,
      },
      surface: {
        card: "bg-teal-950/80 border border-teal-800 backdrop-blur-md shadow-lg",
        glassDock: "bg-teal-950/80 backdrop-blur-xl border border-teal-800/50",
        modal: "bg-teal-950 border border-teal-800 shadow-2xl",
        tooltip: "bg-teal-50 text-teal-900",
        dropdown: "bg-teal-900 border border-teal-800 shadow-xl",
      },
      backgroundAnimation: { type: "none", speed: 0, intensity: 0, colors: [] },
      componentLayouts: {
        Sidebar: { width: "240px", position: "left", background: "standard", size: "h-screen", items: [], activeState: "teal" },
        Header: { height: "auto", background: "standard", shadow: "none" },
        MainGrid: { columns: 12, gap: "6", padding: "8" },
        Footer: { height: "auto", background: "transparent", borderTop: "none" },
      },
      featurePositionMap: {},
    },
  },
};

export default themes;
